import { initializeCapabilities } from '@libs/focus-frontend-authorization/src/AuthorizationService';
import DatesService from '@libs/focus-frontend-dates/src';
import DialogsService from '@libs/focus-frontend-dialogs/src';
import services from '@libs/focus-frontend-services';
import { getTimeZone } from '@libs/focus-frontend-services/src/AccountService/AccountService';
import { initialize as initializeAnalyticsService } from '@libs/focus-frontend-services/src/AnalyticsService';
import { getAccessToken } from '@libs/focus-frontend-services/src/AuthenticationService';
import ConsentService from '@libs/focus-frontend-services/src/ConsentService';
import { FeatureFlag, get } from '@libs/focus-frontend-services/src/FeatureFlagService';
import HelpWidgetService from '@libs/focus-frontend-services/src/HelpWidgetService';
import InAppChatService from '@libs/focus-frontend-services/src/InAppChatService';
import InAppSalesChatService from '@libs/focus-frontend-services/src/InAppSalesChatService';
import LoggingService from '@libs/focus-frontend-services/src/LoggingService';
import RealUserMonitoringService from '@libs/focus-frontend-services/src/RealUserMonitoringService';
import { InitialState } from '@libs/focus-frontend-types/src/@teamleader/api/initialState';
import { loadScript } from '@libs/focus-frontend-utils/src/dom';
import { legacyJavascriptPathGenerator } from '@libs/focus-frontend-utils/src/helpers';
import { camelizeKeys } from 'humps';
import { Settings } from 'luxon';

import createReactRenderQueue from './infrastructure/Reactify/ReactRenderQueue';
import initializePlanhatNpsSurvey from './planhat';
import render from './render';
import mapTLGLOBALS from './window/TLGLOBALS';
import mapInitialState from './window/__INITIAL_STATE__';
import mapEnvironmentVariables from './window/environment';

import './saasquatch';
import './microFrontendListeners';

const PAGE_LOADED_AT = new Date().toISOString();

const enum AccessTokenError {
  Unauthorized = 'Unauthorized',
  ErrorFetchingAccessToken = 'Error fetching access token',
}

const fetchInitialState = (accessToken: string) => {
  return fetch('/api/initialState', {
    method: 'POST',
    headers: {
      authorization: `Bearer ${accessToken}`,
      'x-tl-frontend-version': environment.BUILD_IDENTIFIER,
      'x-tl-page-loaded-at': PAGE_LOADED_AT,
    },
  })
    .then((response) => response.json())
    .then((response) => {
      const { data: camelizedData } = camelizeKeys(response) as { data: InitialState };

      /**
       * feature flags, web app preference and language keys should not be camelized for now
       * but if we want a consistent API response in /initialState, we should camelize them / use actual API SDK's to
       * fetch these values
       */
      return {
        data: {
          ...camelizedData,
          user: {
            ...camelizedData.user,
            featureFlags: response.data.user.feature_flags,
            featureFlagsLegacy: response.data.user.feature_flags_legacy,
            preferences: {
              ...camelizedData.user.preferences,
              webApp: response.data.user.preferences.web_app,
            },
          },
          account: { ...camelizedData.account, featureFlags: response.data.account.feature_flags },
          global: {
            ...camelizedData.global,
            languages: response.data.global.languages,
          },
        },
      };
    });
};

const initializeServices = (initialState: InitialState) => {
  ConsentService.initialize(initialState.user.language, initialState.account.intId, initialState.user.intId);
  initializeAnalyticsService();
  InAppChatService.initialize();
  InAppSalesChatService.initialize();
  HelpWidgetService.initialize();
  LoggingService.initialize();
  RealUserMonitoringService.initialize();

  window.services = { ...(window.services || {}), ...services };
  window.services.dialogs = DialogsService;
  window.services.dates = DatesService;
};

// top-level awaits are not supported by webpack 4, so we need to wrap everything inside of an IIFE
(async function () {
  let accessToken;
  try {
    accessToken = await getAccessToken();
  } catch (error: any) {
    if (error.message === AccessTokenError.Unauthorized) {
      const intendedPath = [window.top!.location.pathname, window.top!.location.search, window.top!.location.hash]
        .filter((part) => part)
        .join('');

      if (!intendedPath || intendedPath === '/') {
        window.top!.location = environment.AUTH_URL;
      } else {
        window.top!.location = `${environment.AUTH_URL}?intended=${encodeURIComponent(intendedPath)}`;
      }

      return;
    }

    throw new Error(error);
  }

  const { data: initialState } = await fetchInitialState(accessToken);

  document.documentElement.lang = initialState.user.language;
  window.TLGLOBALS = mapTLGLOBALS(initialState);
  window.__localeId__ = initialState.user.language;

  window.__INITIAL_STATE__ = mapInitialState(initialState);
  window.ReactRenderQueue = createReactRenderQueue();
  window.environment = mapEnvironmentVariables();

  Settings.defaultZone = getTimeZone();
  Settings.defaultLocale = window.__localeId__ || 'en';

  initializePlanhatNpsSurvey();

  const hasFrontendCapabilityBasedAuthorization = get(FeatureFlag.EnableFrontendCapabilityBasedAuthorization);
  if (hasFrontendCapabilityBasedAuthorization) {
    try {
      await initializeCapabilities();
    } catch {
      window.top!.location = '/error.html';
      return;
    }
  }

  initializeServices(initialState);
  await loadScript(legacyJavascriptPathGenerator());

  // posts a message for the dev tools chrome extension to intercept and use the data
  window.postMessage(
    {
      type: 'FROM_PAGE',
      data: {
        environment,
        featureFlags: {
          account: window.top!.__INITIAL_STATE__.account.featureFlags,
          user: window.top!.__INITIAL_STATE__.user.featureFlags,
        },
      },
    },
    environment.APP_URL,
  );

  const { default: App, mapAppProps } = await import('./App');
  const MOUNT_NODE = document.getElementById('react-root');
  render(<App {...mapAppProps(initialState)} />, MOUNT_NODE!);
})();
